import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useContent } from '../../hooks/useContent';

export default function AboutFounder() {
  const { content } = useContent();
  const a = content?.aboutFounder;
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const imageY = useTransform(scrollYProgress, [0, 1], ['-5%', '5%']);

  return (
    <section ref={sectionRef} style={{ padding: '100px 0', background: 'linear-gradient(180deg, #0D0D1F 0%, #1A0F2E 100%)', isolation: 'isolate' }}>
      <div className="container">
        <div className="about-founder-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center', position: 'relative' }}>

          {/* Image side with parallax */}
          <motion.div style={{ y: imageY, position: 'relative', zIndex: 1, isolation: 'isolate' }}>
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              style={{ position: 'relative' }}
            >
              {/* Professional background */}
              <div style={{
                position: 'absolute', inset: '-20px',
                borderRadius: 28,
                background: 'linear-gradient(145deg, #1e1030 0%, #0d0d1f 45%, #12091f 70%, #1a0f2e 100%)',
                zIndex: 0,
              }}>
                <div style={{ position: 'absolute', inset: 0, borderRadius: 28, background: 'radial-gradient(ellipse at 50% 25%, rgba(232,135,26,0.2) 0%, transparent 60%)' }} />
                <div style={{ position: 'absolute', inset: 0, borderRadius: 28, backgroundImage: 'radial-gradient(circle, rgba(232,135,26,0.07) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                {[{ top: 10, left: 10 }, { top: 10, right: 10 }, { bottom: 10, left: 10 }, { bottom: 10, right: 10 }].map((pos, i) => (
                  <div key={i} style={{ position: 'absolute', ...pos, width: 18, height: 18, border: '1.5px solid rgba(232,135,26,0.4)', borderRadius: 3, transform: 'rotate(45deg)' }} />
                ))}
                <div style={{ position: 'absolute', top: '8%', left: '50%', transform: 'translateX(-50%)', width: '75%', height: '60%', borderRadius: '50%', border: '1px solid rgba(232,135,26,0.1)' }} />
                <div style={{ position: 'absolute', top: '14%', left: '50%', transform: 'translateX(-50%)', width: '55%', height: '45%', borderRadius: '50%', border: '1px solid rgba(232,135,26,0.07)' }} />
              </div>

              <motion.div
                animate={{ boxShadow: ['0 0 30px rgba(232,135,26,0.15)', '0 0 60px rgba(232,135,26,0.35)', '0 0 30px rgba(232,135,26,0.15)'] }}
                transition={{ duration: 4, repeat: Infinity }}
                style={{
                  width: '100%', paddingBottom: '100%', position: 'relative',
                  borderRadius: 16, overflow: 'hidden',
                  border: '1.5px solid rgba(232,135,26,0.4)',
                  zIndex: 1,
                }}
              >
                {/* Actual founder image */}
                <img
                  src="/founder.jpeg"
                  alt="Dr. Raghav Taparia"
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'center 8%',
                    zIndex: 1,
                    transform: 'scale(1.15)',
                    transformOrigin: 'center 12%',
                  }}
                />
                {/* Bottom gradient overlay */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%',
                  background: 'linear-gradient(to top, rgba(13,13,31,0.85), transparent)',
                  zIndex: 3,
                }} />
                {/* Name tag at bottom */}
                <div style={{
                  position: 'absolute', bottom: 20, left: 20, zIndex: 4,
                }}>
                  <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 16, color: '#E8871A', lineHeight: 1.2 }}>{a?.name ?? 'Dr. Raghav Taparia'}</p>
                  <p style={{ fontSize: 11, color: '#FDF6EC', opacity: 0.6, letterSpacing: 2, marginTop: 3 }}>{(a?.role ?? 'CERTIFIED VASTU CONSULTANT').toUpperCase()}</p>
                </div>
              </motion.div>

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0, rotate: -10 }}
                animate={isInView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
                transition={{ delay: 0.6, type: 'spring', stiffness: 200 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
                style={{
                  position: 'absolute', bottom: -20, right: -20,
                  background: 'linear-gradient(135deg, #E8871A, #c4700f)',
                  borderRadius: 12, padding: '16px 20px', textAlign: 'center', cursor: 'default',
                  zIndex: 10,
                }}
              >
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, color: '#0D0D1F', lineHeight: 1 }}>📜</p>
                <p style={{ fontSize: 11, color: '#0D0D1F', fontWeight: 700, marginTop: 4, letterSpacing: 1 }}>CERTIFIED</p>
                <p style={{ fontSize: 10, color: '#0D0D1F', opacity: 0.7 }}>Vastu Consultant</p>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Content side */}
          <div style={{ position: 'relative', zIndex: 10, isolation: 'isolate' }}>
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <p className="section-eyebrow">{a?.eyebrow ?? 'About the Founder'}</p>
              <h2 className="section-title">{a?.title ?? 'Engineer · Entrepreneur · Vastu Consultant'}</h2>
              <motion.div
                initial={{ width: 0 }}
                animate={isInView ? { width: 60 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                style={{ height: 2, background: 'linear-gradient(90deg, #E8871A, transparent)', margin: '20px 0' }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <p style={{ fontSize: 15, lineHeight: 1.9, opacity: 0.75, marginBottom: 20 }}>
                After completing an Engineering degree and managing a family business in the biomass briquettes and pellets industry, the founder discovered the profound impact of Vastu Shastra on spaces, energy, and success.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.9, opacity: 0.75, marginBottom: 32 }}>
                Trained under renowned Vastu expert Vastu RaviRaj Sir, with extensive practical exposure in analyzing and resolving Vastu-related concerns. Today, combining technical background, business experience, and Vastu expertise to provide practical, result-oriented solutions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
                {(a?.highlights ?? [
                  'Engineering Graduate',
                  'Entrepreneur — Biomass Briquettes & Pellets Industry',
                  'Certified Vastu Consultant (Cert. No. 12345)',
                  'Trained under Vastu RaviRaj Sir',
                  'Practical & Result-Oriented Approach',
                ]).map((item: string, i: number) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.6 + i * 0.1 }}
                    style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}
                  >
                    <motion.span
                      animate={{ rotate: [0, 180, 360] }}
                      transition={{ duration: 4, delay: i * 0.5, repeat: Infinity, ease: 'linear' }}
                      style={{ color: '#E8871A', marginTop: 2, display: 'inline-block' }}
                    >✦</motion.span>
                    <span style={{ fontSize: 14, opacity: 0.8 }}>{item}</span>
                  </motion.div>
                ))}
              </div>
              <Link to={a?.cta?.path ?? '/about'} className="btn-primary">{a?.cta?.label ?? 'Know More'}</Link>
            </motion.div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 1024px) {
          .about-founder-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
        }
        @media (max-width: 768px) {
          .about-founder-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
      `}</style>
    </section>
  );
}
